import { store } from '../store';
import { encryptData, decryptData, decryptKeyData, encryptKeyData } from '../utils/encryption';
import { secureStore } from '../utils/secureStore';
import { BankDetail, BankDetailFormData } from '../types/bankDetails';
import { config } from '../config';
import { handleSessionExpired } from './sessionHandler';

const API_URL = config.API_URL;

export class BankDetailsService {
    // Removed authToken; authentication now via session cookie

    private static async request<T>(
        endpoint: string,
        method: string = 'GET',
        body?: any
    ): Promise<T> {
        // No token needed; authentication via session cookie
        try {
            const response = await fetch(`${API_URL}/bank-details${endpoint}`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include', // Send cookies for session auth
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired();
                }
                console.error('API Error:', data);
                throw new Error(data.message || 'An error occurred');
            }

            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    private static getMasterKey(): string {
        try {
            return secureStore.getVaultKey();
        } catch (error) {
            throw new Error('Vault key not found. Please login again.');
        }
    }

    static async getAllBankDetails(workspaceId?: string): Promise<BankDetail[]> {
        try {
            const details = await this.request<BankDetail[]>(`?workspace_id=${workspaceId}`);
            return details.map(detail => {
                // A bank detail is shared if it has a shared_key or is explicitly marked as shared
                const isShared = !!detail.shared_key || detail.is_shared;
                // For shared items, we need to decrypt the shared_key
                const sharedKey = isShared && detail.shared_key ? decryptKeyData(detail.shared_key) : undefined;
                return this.decryptBankDetail(detail, isShared, sharedKey);
            });
        } catch (error) {
            console.error('Failed to fetch bank details:', error);
            throw error;
        }
    }

    static async createBankDetail(detail: BankDetailFormData): Promise<BankDetail> {
        try {
            const encryptedDetail = this.encryptBankDetail(detail);
            const response = await this.request<BankDetail>('', 'POST', encryptedDetail);
            return this.decryptBankDetail(response);
        } catch (error) {
            console.error('Create bank detail error:', error);
            throw error;
        }
    }

    static async updateBankDetail(id: string, detail: BankDetailFormData): Promise<BankDetail> {
        try {
            const encryptedDetail = this.encryptBankDetail(detail, detail.shared_key);
            const response = await this.request<BankDetail>(`/${id}`, 'PUT', encryptedDetail);
            return this.decryptBankDetail(response);
        } catch (error) {
            console.error('Update bank detail error:', error);
            throw error;
        }
    }

    static async deleteBankDetail(id: string, isShared: boolean = false): Promise<void> {
        await this.request(`/${id}?isShared=${isShared}`, 'DELETE');
    }

    static async getBankDetailById(id: string, isShared: boolean = false, sharedKey?: string): Promise<BankDetail> {
        const response = await this.request<BankDetail>(`/${id}`);

        if (isShared && sharedKey) {
            return this.decryptBankDetail(response, isShared, sharedKey);
        }
        
        return this.decryptBankDetail(response);
    }

    private static decryptBufferData(encryptedBuffer: any, isShared: boolean = false, sharedKey?: string): string {
        if (!encryptedBuffer) return '';

        try {
            if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
                const uint8Array = new Uint8Array(encryptedBuffer.data);
                const encryptedString = new TextDecoder().decode(uint8Array);
                return isShared && sharedKey ? decryptData(encryptedString, sharedKey) : decryptData(encryptedString, this.getMasterKey());
            }

            if (typeof encryptedBuffer === 'string') {
                return isShared && sharedKey ? decryptData(encryptedBuffer, sharedKey) : decryptData(encryptedBuffer, this.getMasterKey());
            }

            return '';
        } catch (error) {
            console.error('Error decrypting buffer:', error);
            return '';
        }
    }

    private static decryptBankDetail(detail: BankDetail, isShared: boolean = false, sharedKey?: string): BankDetail {
        let decryptionKey = '';
        if (detail.shared_key) {
            // If bank detail has its own shared_key, use that
            decryptionKey = decryptKeyData(detail.shared_key);
        } else {
            // Otherwise use provided sharedKey or master key
            decryptionKey = sharedKey || this.getMasterKey();
        }

        const shouldUseSharedKey = isShared || !!detail.shared_key;
        console.log("Using decryption key:", decryptionKey);
        
        const decryptField = (value?: string | null) => {
            if (!value) return null;
            try {
                return this.decryptBufferData(value, shouldUseSharedKey, decryptionKey);
            } catch (error) {
                console.error('Failed to decrypt field:', error);
                return '(Decryption failed)';
            }
        };

        return {
            ...detail,
            encrypted_routing_number: decryptField(detail.encrypted_routing_number) || '',
            encrypted_account_number: decryptField(detail.encrypted_account_number) || '',
            encrypted_swift_code: decryptField(detail.encrypted_swift_code) || '',
            encrypted_iban_number: decryptField(detail.encrypted_iban_number) || '',
            encrypted_pin: decryptField(detail.encrypted_pin) || '',
            encrypted_branch_address: decryptField(detail.encrypted_branch_address) || '',
            encrypted_branch_phone: decryptField(detail.encrypted_branch_phone) || '',
            encrypted_notes: decryptField(detail.encrypted_notes) || '',
            categories: detail.categories || [],
            favourite: detail.favourite || false,
            shared_key: detail.shared_key ? decryptKeyData(detail.shared_key) : '',
            is_owner: detail.is_owner || false
        };
    }

    private static encryptBankDetail(detail: Partial<BankDetailFormData>, sharedKey?: string): any {
        // Determine which key to use for encryption
        let encryptionKey = '';
        if (detail.shared_key) {
            // If detail has its own shared_key, use that
            encryptionKey = detail.shared_key;
        } else if (sharedKey) {
            // If a shared key is provided, use that
            encryptionKey = sharedKey;
        } else {
            // Otherwise use master key
            encryptionKey = this.getMasterKey();
        }

        const encryptField = (value?: string) => value ? encryptData(value, encryptionKey) : null;

        // IMPORTANT: Always set shared_key for ALL bank entries
        // - For new items: Encrypt owner's vault key with SECRET_KEY
        // - For shared items: Use the provided shared_key
        // This ensures consistency and enables sharing/password reset
        let finalSharedKey = '';
        if (detail.shared_key) {
            // Use provided shared key (for updates or already-shared items)
            finalSharedKey = encryptKeyData(detail.shared_key);
        } else if (!detail.isSharedUpdate) {
            // For NEW items or personal items, encrypt vault key with SECRET_KEY
            finalSharedKey = encryptKeyData(encryptionKey);
        }

        return {
            name: detail.name || null,
            bank_name: detail.bank_name || null,
            account_type: detail.account_type || null,
            encrypted_routing_number: encryptField(detail.encrypted_routing_number),
            encrypted_account_number: encryptField(detail.encrypted_account_number),
            encrypted_swift_code: encryptField(detail.encrypted_swift_code),
            encrypted_iban_number: encryptField(detail.encrypted_iban_number),
            encrypted_pin: encryptField(detail.encrypted_pin),
            encrypted_branch_address: encryptField(detail.encrypted_branch_address),
            encrypted_branch_phone: encryptField(detail.encrypted_branch_phone),
            encrypted_notes: encryptField(detail.encrypted_notes),
            category_ids: detail.category_ids || [],
            remove_category_ids: detail.remove_category_ids || [],
            category_id: detail.category_id || null,
            favourite: detail.favourite || false,
            isSharedUpdate: detail.isSharedUpdate || false,
            shared_key: finalSharedKey,
            itemType: detail.itemType || 'bank',
            is_owner: detail.is_owner || false,
            workspace_id: detail.workspace_id || null
        };
    }

    static async getBankDetailsNotInThisVault(categoryId: string): Promise<BankDetail[]> {
        const response = await this.request<BankDetail[]>(`/not-in-this-vault/${categoryId}`);
        return response.map(detail => this.decryptBankDetail(detail));
    }
}

export default BankDetailsService; 